import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_CONTROL_FOCUS_WITHIN_CLASS,
} from '@/components/ui/form-control-styles'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/toast'
import { DatePicker } from '@/components/shared'
import {
  useContractInterventions,
  useUpdateContractStatus,
} from '@/hooks/useContracts'
import {
  isoDateToLocalDate,
  localDateToIsoDate,
  todayIsoDate,
} from '@/lib/date'
import { cn } from '@/lib/utils'
import { getApiErrorMessage } from '@/services/api'
import type { Contract } from '@/types'

const dialogContentClass =
  'max-w-[640px] gap-0 rounded-2xl border-[#e7e9ef] bg-white p-8 shadow-[0_8px_16px_-2px_rgba(38,45,66,0.1),0_1px_4px_-2px_rgba(38,45,66,0.05)] sm:rounded-2xl [&>button]:hidden'

const fieldLabelClass =
  'text-xs font-medium leading-4 text-[#171a20] peer-disabled:cursor-not-allowed peer-disabled:opacity-70'

const datePickerClass = cn(
  'h-10 rounded-xl border border-transparent bg-[#f5f6fa] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none [&_input]:text-sm [&_input]:font-normal [&_input]:leading-[1.4] [&_input]:text-[#171a20] [&_input]:placeholder:text-[#6c7285] [&_button]:text-[#171a20]',
  FORM_CONTROL_FOCUS_WITHIN_CLASS
)

const textareaClass = cn(
  'min-h-20 resize-none rounded-xl border border-transparent bg-[#f5f6fa] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none placeholder:text-[#6c7285]',
  FORM_CONTROL_FOCUS_CLASS
)

const outlineButtonClass = cn(
  'h-11 rounded-xl border-[#e7e9ef] bg-white px-3 py-3 text-sm font-medium leading-none text-[#171a20] normal-case hover:bg-[#f4f5f8]',
  FORM_CONTROL_FOCUS_CLASS
)

const primaryButtonClass = cn(
  'h-11 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-3 py-3 text-sm font-medium leading-none text-white normal-case hover:bg-[#e94d17]',
  FORM_CONTROL_FOCUS_CLASS
)

interface Props {
  contract: Contract | null
  onOpenChange: (open: boolean) => void
  onTerminated?: () => void
}

export function TerminateContractDialog({
  contract,
  onOpenChange,
  onTerminated,
}: Props) {
  const { t } = useTranslation(['contracts', 'common'])
  const updateStatus = useUpdateContractStatus(contract?.id ?? '')
  const [terminationDate, setTerminationDate] = useState(todayIsoDate())
  const [reason, setReason] = useState('')
  const [errors, setErrors] = useState<{
    terminationDate?: string
    reason?: string
  }>({})

  const futureJobsDateFrom = useMemo(() => {
    const date = isoDateToLocalDate(terminationDate)
    if (!date) return ''
    const nextDay = new Date(date)
    nextDay.setDate(nextDay.getDate() + 1)
    return localDateToIsoDate(nextDay)
  }, [terminationDate])

  const futureJobs = useContractInterventions(
    futureJobsDateFrom ? contract?.id : undefined,
    {
      page: 1,
      limit: 1,
      status: ['planned', 'assigned', 'in_progress'],
      date_from: futureJobsDateFrom || undefined,
      sort_column: 'scheduled_date',
      sort_order: 'asc',
    }
  )
  const futureJobsCount = futureJobs.data?.metadata?.total ?? 0

  const validate = () => {
    const next: { terminationDate?: string; reason?: string } = {}
    if (!terminationDate)
      next.terminationDate = t('detail.terminate.dateRequired')
    if (!reason.trim()) next.reason = t('detail.terminate.reasonRequired')
    else if (reason.trim().length > 2000) {
      next.reason = t('detail.terminate.reasonTooLong')
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const confirm = async () => {
    if (!contract) return
    if (!validate()) return
    try {
      await updateStatus.mutateAsync({
        status: 'terminated',
        termination_date: terminationDate,
        termination_reason: reason.trim(),
      })
      toast({ variant: 'success', title: t('detail.terminate.success') })
      onTerminated?.()
      onOpenChange(false)
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('detail.terminate.error')),
      })
    }
  }

  if (!contract) return null

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className={dialogContentClass}>
        <div className='flex flex-col gap-6'>
          <DialogHeader className='gap-3 space-y-0 text-left'>
            <DialogTitle className='font-heading text-base font-semibold leading-[1.2] text-[#171a20]'>
              {t('detail.terminate.title')}
            </DialogTitle>
            <p className='text-sm font-normal leading-[1.4] text-[#171a20] normal-case'>
              {t('detail.terminate.confirmMessage', {
                number: contract?.number ?? t('detail.fallbackNumber'),
              })}
            </p>
          </DialogHeader>

          <div className='space-y-1'>
            <Label
              htmlFor='contract-termination-date'
              className={fieldLabelClass}
            >
              {t('detail.terminate.terminationDate')}
              <span className='ml-1 text-[#d63b33]'>*</span>
            </Label>
            <DatePicker
              id='contract-termination-date'
              value={terminationDate}
              onChange={value => {
                setTerminationDate(value)
                setErrors(current => ({
                  ...current,
                  terminationDate: undefined,
                }))
              }}
              className={cn(
                datePickerClass,
                errors.terminationDate && 'border border-destructive'
              )}
              error={!!errors.terminationDate}
            />
            {errors.terminationDate && (
              <p className='text-xs leading-4 text-destructive'>
                {errors.terminationDate}
              </p>
            )}
          </div>

          <div className='space-y-1'>
            <Label
              htmlFor='contract-termination-reason'
              className={fieldLabelClass}
            >
              {t('detail.terminate.reason')}
            </Label>
            <Textarea
              id='contract-termination-reason'
              value={reason}
              maxLength={2000}
              onChange={event => {
                setReason(event.target.value)
                setErrors(current => ({ ...current, reason: undefined }))
              }}
              className={cn(
                textareaClass,
                errors.reason && 'border border-destructive'
              )}
              placeholder={t('detail.terminate.reasonPlaceholder')}
            />
            <div className='flex items-center justify-between gap-3'>
              {errors.reason ? (
                <p className='text-xs leading-4 text-destructive'>
                  {errors.reason}
                </p>
              ) : (
                <span />
              )}
              <p className='text-xs text-muted-foreground'>
                {t('detail.terminate.reasonCount', { count: reason.length })}
              </p>
            </div>
          </div>

          {futureJobsCount > 0 && (
            <div className='rounded-xl bg-[#fef3c7] p-3 text-sm font-normal leading-[1.4] text-[#171a20] normal-case'>
              {t('detail.terminate.futureJobsWarning', {
                count: futureJobsCount,
              })}
            </div>
          )}
          <DialogFooter className='flex-row justify-end gap-4 space-x-0'>
            <Button
              variant='outline'
              className={outlineButtonClass}
              onClick={() => onOpenChange(false)}
              disabled={updateStatus.isPending}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              className={primaryButtonClass}
              onClick={confirm}
              disabled={updateStatus.isPending}
            >
              {t('detail.terminate.confirm')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default TerminateContractDialog
